import { attribute, attributeGetter, Component, type Emits } from "@bodil/dom/component";
import type { Role } from "aria-attribute-types";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { createRef, ref } from "lit/directives/ref.js";

import styles from "./checkbox.css?inline";

declare global {
    interface HTMLElementTagNameMap {
        "btk-checkbox": BtkCheckbox;
    }
}

@customElement("btk-checkbox")
export class BtkCheckbox extends Component {
    emits!: Emits<"change">;

    @attribute({ type: Boolean }) accessor checked = false;
    @attribute({ type: Boolean }) accessor indeterminate = false;
    @attribute({ type: Boolean }) accessor disabled = false;
    @attribute accessor name = "";

    @attributeGetter get role(): Role {
        return "checkbox";
    }

    @attributeGetter({ reactive: true }) get ariaChecked(): "true" | "false" | "mixed" {
        return this.indeterminate ? "mixed" : this.checked ? "true" : "false";
    }

    private readonly input = createRef<HTMLInputElement>();

    focus(options: FocusOptions) {
        this.input.value?.focus(options);
    }

    blur() {
        this.input.value?.blur();
    }

    click() {
        this.input.value?.click();
    }

    private handleChange(_e: Event) {
        this.checked = this.input.value?.checked === true;
        this.indeterminate = this.input.value?.indeterminate === true;
        this.emit("change");
    }

    render() {
        return html`
            <label part="label">
                <input
                    ${ref(this.input)}
                    part="input"
                    type="checkbox"
                    name=${this.name}
                    .checked=${live(this.checked)}
                    .indeterminate=${live(this.indeterminate)}
                    ?disabled=${this.disabled}
                    @change=${this.handleChange}
                />
                <slot></slot>
            </label>
        `;
    }

    static styles = styles;
}
